import { queryCommonFlags } from "@/api/constants";
import { useCallback, useMemo } from "react";
import { getSubDepartments } from "@/modules/lead-page/api/lead-page.v2.api";
import { useState } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";

const useFetchSubDepartments = ({
  organizationUId,
  hasTarget,
}: {
  organizationUId?: string;
  hasTarget?: boolean;
} = {}) => {
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
  });

  const [searchTerm, setSearchTerm] = useState("");

  const handlePageChange = useCallback((page: number) => {
    setPagination((prev: { page: number; pageSize: number }) => ({
      ...prev,
      page: page,
    }));
  }, []);

  const queryParams = {
    pageSize: pagination.pageSize,
    ...(searchTerm && { name: searchTerm }),
    ...(organizationUId && { organizationUId }),
    ...(hasTarget !== undefined && { hasTarget }),
  };

  const { data: firstPageData, isLoading: isLoadingFirstPage } = useQuery(
    ["subDepartments", pagination, searchTerm, organizationUId, hasTarget],
    () =>
      getSubDepartments({
        page: pagination.page,
        ...queryParams,
      }),
    {
      ...queryCommonFlags,
    }
  );

  const total = firstPageData?.data?.total ?? 0;
  const totalPages =
    hasTarget !== undefined ? Math.ceil(total / pagination.pageSize) : 1;
  const remainingPages =
    totalPages > 1
      ? Array.from({ length: totalPages - 1 }, (_, i) => i + 2)
      : [];

  const remainingPagesQueries = useQueries({
    queries: remainingPages.map((page) => ({
      queryKey: [
        "subDepartments",
        { ...pagination, page },
        searchTerm,
        organizationUId,
        hasTarget,
      ],
      queryFn: () =>
        getSubDepartments({
          page,
          ...queryParams,
        }),
      enabled: total > 0,
      ...queryCommonFlags,
    })),
  });

  const isLoading =
    isLoadingFirstPage || remainingPagesQueries.some((q) => q.isLoading);

  const subDepartments = useMemo(() => {
    const firstPageResults = firstPageData?.data?.results ?? [];
    if (hasTarget === undefined) return firstPageResults;
    const remainingResults = remainingPagesQueries.flatMap(
      (q) => q.data?.data?.results ?? []
    );
    return [...firstPageResults, ...remainingResults];
  }, [firstPageData, remainingPagesQueries, hasTarget]);

  return {
    subDepartments,
    total: firstPageData?.data?.total,
    subDepartmentsLoading: isLoading,
    handlePageChange,
    pagination,
    searchTerm,
    setSearchTerm,
  };
};
export default useFetchSubDepartments;
